import { type ReactNode } from 'react';

import { cn } from '../lib/cn.js';

interface StatPillProps {
  value: ReactNode;
  label?: ReactNode;
  /** Visual tone — defaults to neutral. */
  tone?: 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
  /** Show a small dot to the left of the value. */
  withDot?: boolean;
  className?: string;
  title?: string;
}

const TONE_CLASS: Record<NonNullable<StatPillProps['tone']>, string> = {
  neutral: 'bg-surface-muted text-fg-secondary',
  primary: 'bg-primary-subtle text-primary',
  success: 'bg-success-subtle text-success',
  warning: 'bg-warning-subtle text-warning',
  danger: 'bg-danger-subtle text-danger',
  info: 'bg-info-subtle text-info',
};

const DOT_CLASS: Record<NonNullable<StatPillProps['tone']>, string> = {
  neutral: 'bg-fg-muted',
  primary: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
};

/**
 * Small numeric pill ("3 open" / "—"), reused for placeholder columns
 * in the buildings list (Work Orders, Violations, Compliance) and for
 * the BIN/BBL chips on the building detail page.
 */
export function StatPill({
  value,
  label,
  tone = 'neutral',
  withDot,
  className,
  title,
}: StatPillProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex h-6 items-center gap-1.5 rounded-full px-2 text-xs font-semibold tabular-nums',
        TONE_CLASS[tone],
        className,
      )}
    >
      {withDot ? (
        <span className={cn('h-1.5 w-1.5 rounded-full', DOT_CLASS[tone])} aria-hidden />
      ) : null}
      <span>{value}</span>
      {label ? <span className="text-fg-muted">{label}</span> : null}
    </span>
  );
}
